import * as React from 'react';

import { cn, FontSemiBold } from '@rematter/ui-web';
import { ContentCard } from '@rematter/ui-web';
import startCase from 'lodash/startCase';
import { LucideEye, LucideEyeOff, LucideXCircle } from 'lucide-react';

import { useDebouncedEffect } from '~/shared/hooks/useDebounce';
import { useTranslations } from '~/shared/i18n/useTranslations';

export type NotesLabels = {
  internalNotes: string;
  externalNotes: string;
  discardChanges: string;
  saveNotes: string;
  clearAll: string;
  addNotesPlaceholder: string;
  hiddenFrom: (counterparty: string) => string;
  visibleTo: (counterparty: string) => string;
};

export type NotesInputProps = {
  title: string;
  counterparty: 'supplier' | 'customer';
  visibility: 'visible' | 'hidden';
  value?: string;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  onRequestSave?: () => void;
  isDisabled?: boolean;
  labels: NotesLabels;
};

const NotesInput = (props: NotesInputProps) => {
  const counterparty = startCase(props.counterparty);
  const labels = props.labels;

  return (
    <div
      className={cn(
        '@NotesInput flex flex-col first:rounded-tl-lg last:rounded-tr-lg',
        'focus-within:ring-4 focus-within:ring-primary/40 focus-within:outline-2 focus-within:outline-primary',
      )}
    >
      <div className={'@_Header flex justify-between px-5 pt-4 pb-2'}>
        <div className={'flex items-center gap-3'}>
          <FontSemiBold className={'truncate text-sm'}>
            {props.title}
          </FontSemiBold>
          <div
            className={cn(
              'rounded bg-neutral/5 px-1.5 py-0.5',
              props.visibility === 'visible' && 'bg-warning/10 text-warning',
            )}
          >
            <span
              className={
                'flex items-center gap-1 truncate text-[11px] opacity-80'
              }
            >
              {props.visibility === 'hidden' && <LucideEyeOff size={12} />}
              {props.visibility === 'visible' && <LucideEye size={12} />}
              {props.visibility === 'hidden'
                ? labels.hiddenFrom(counterparty)
                : labels.visibleTo(counterparty)}
            </span>
          </div>
        </div>
        {props.value && !props.isDisabled && (
          <button
            tabIndex={-1}
            className={cn(
              'flex cursor-pointer items-center gap-1 truncate rounded border-none px-2 py-1 text-xs opacity-70 outline-none',
              'hover:bg-primary/10 hover:text-primary hover:opacity-100',
            )}
            onClick={() => props.onChange?.('')}
          >
            <LucideXCircle size={12} />
            {labels.clearAll}
          </button>
        )}
      </div>
      <div className={'@_Content flex h-full'}>
        <textarea
          aria-label="Notes"
          className={
            'size-full border-none border-neutral p-5 pt-0 font-sans text-sm font-normal outline-none placeholder:text-neutral/20'
          }
          autoFocus={props.autoFocus}
          value={props.value}
          onChange={(e) => props.onChange?.(e.target.value)}
          placeholder={labels.addNotesPlaceholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              props.onRequestSave?.();
            }
          }}
          disabled={props.isDisabled}
        />
      </div>
    </div>
  );
};

export type EntityTabNotesProps = {
  counterparty: 'supplier' | 'customer';
  internalNotes: string | undefined | null;
  externalNotes: string | undefined | null;
  onSave?: (notes: { internalNotes: string; externalNotes: string }) => void;
  onChange?: (notes: { internalNotes: string; externalNotes: string }) => void;
  className?: string;
  isReadonly?: boolean;
  labels?: NotesLabels;
};

export const EntityTabNotes = (props: EntityTabNotesProps) => {
  const t = useTranslations();

  const labels = props.labels ?? t.entityTabNotes;

  const initialRef = React.useRef({
    internalNotes: props.internalNotes ?? '',
    externalNotes: props.externalNotes ?? '',
  });
  const [internalNotes, setInternalNotes] = React.useState(
    initialRef.current.internalNotes,
  );
  const [externalNotes, setExternalNotes] = React.useState(
    initialRef.current.externalNotes,
  );

  React.useEffect(() => {
    const nextInternal = props.internalNotes ?? '';
    const nextExternal = props.externalNotes ?? '';
    if (
      nextInternal === initialRef.current.internalNotes &&
      nextExternal === initialRef.current.externalNotes
    )
      return;
    setInternalNotes(nextInternal);
    setExternalNotes(nextExternal);
    initialRef.current = {
      internalNotes: nextInternal,
      externalNotes: nextExternal,
    };
  }, [props.internalNotes, props.externalNotes]);

  useDebouncedEffect(
    () => {
      if (props.isReadonly || !props.onSave) return;
      if (
        internalNotes === initialRef.current.internalNotes &&
        externalNotes === initialRef.current.externalNotes
      )
        return;
      const prev = initialRef.current;
      initialRef.current = { internalNotes, externalNotes };
      Promise.resolve(props.onSave({ internalNotes, externalNotes })).catch(
        () => {
          initialRef.current = prev;
        },
      );
    },
    500,
    [internalNotes, externalNotes],
  );

  return (
    <div className={cn('@EntityTabNotes', 'flex h-full p-5', props.className)}>
      <ContentCard className="flex size-full flex-col overflow-visible p-0">
        <div className="grid h-full grid-cols-[1fr_1px_1fr]">
          <NotesInput
            visibility={'hidden'}
            title={labels.internalNotes}
            counterparty={props.counterparty}
            autoFocus
            value={internalNotes}
            onChange={(value) => {
              setInternalNotes(value);
              props.onChange?.({ internalNotes: value, externalNotes });
            }}
            isDisabled={props.isReadonly}
            labels={labels}
          />
          <div className={'border-l border-neutral/10'} />
          <NotesInput
            visibility={'visible'}
            title={labels.externalNotes}
            counterparty={props.counterparty}
            value={externalNotes}
            onChange={(value) => {
              setExternalNotes(value);
              props.onChange?.({ internalNotes, externalNotes: value });
            }}
            isDisabled={props.isReadonly}
            labels={labels}
          />
        </div>
      </ContentCard>
    </div>
  );
};
